import React from 'react';
import { MsFormulaire, OperationMS } from '../types';
import { CHAMP_PHRASE_MAX } from '../domain/operationConsultation';
import { adresseEntiteUneLigne } from '../../../utils/adresse';

interface OperationAcheteurRecapProps {
  operation: OperationMS;
  formData: MsFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: MsFormulaire) => void;
}

// Case de recap LECTURE SEULE (libelle + valeur, "-" si absent), avec son span de colonnes.
// BLINDE : ne rend qu'une chaine/nombre ; tout autre type (objet, tableau) -> "-".
const Champ: React.FC<{ label: string; valeur?: unknown; col: number }> = ({ label, valeur, col }) => {
  const texte = typeof valeur === 'string' || typeof valeur === 'number' ? String(valeur).trim() : '';
  return (
    <div className="form-group" style={{ gridColumn: `span ${col}` }}>
      <label>{label}</label>
      <div className="input-inactif">{texte || '-'}</div>
    </div>
  );
};

// Sous-bloc "Acheteur" du Descriptif administratif.
//  - Identite organisme (nom, siret, adresse, contact...) : recap LECTURE SEULE (operation.organisme).
//  - Signataire + Modalite de facturation : seuls champs SAISIS par l'acheteur (sac formulaire).
// Disposition fidele au xlsx (grille 12 colonnes) : 3 / 4 / 4 / 2 champs.
const OperationAcheteurRecap: React.FC<OperationAcheteurRecapProps> = ({ operation, formData, viewMode, errors = {}, setFormData }) => {
  const org = operation.organisme;
  const adr = org?.adresse; // entite adresse imbriquee (objet, pas une chaine)
  // Adresse du siege social sur UNE ligne, code postal / ville / pays compris : ils n'ont plus de
  // champ propre a cote. Meme forme que l'adresse de l'operation en lecture seule, pour que les deux
  // adresses de l'onglet se lisent pareil.
  const adresseLigne = adresseEntiteUneLigne(adr);

  const update = (cle: keyof MsFormulaire, value: string) => {
    if (setFormData) setFormData({ ...formData, [cle]: value });
  };

  // Champ SAISI (editable). `col` par defaut 6 (signataire / modalite) ; les 4 champs de contact
  // operationnel occupent 3 colonnes, comme les champs de recap qu'ils remplacent.
  const champSaisi = (cle: keyof MsFormulaire, label: string, placeholder: string, col = 6) => {
    const valeur = String(formData[cle] ?? '');
    return (
      <div className="form-group" style={{ gridColumn: `span ${col}` }} key={cle}>
        <label>{label}</label>
        {viewMode ? (
          <div className="input-inactif">{valeur || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={valeur}
              onChange={(e) => update(cle, e.target.value)}
              placeholder={placeholder}
              className={errors[cle] ? 'error' : ''}
            />
            <span className="opms__char-count">{valeur.length}/{CHAMP_PHRASE_MAX}</span>
            {/* Rendu obligatoire : une regle de validation sans message visible bloque l'action
                sans expliquer pourquoi. */}
            {errors[cle] && <span className="error-message">{errors[cle]}</span>}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="opms__admin-grid">
      {/* Identite organisme : recap LECTURE SEULE */}
      <Champ col={6} label="Nom de l'organisme" valeur={org?.nom} />
      <Champ col={3} label="Siret" valeur={org?.siret} />
      <Champ col={3} label="Type d'organisme" valeur={org?.type_organisme} />
      <Champ col={12} label="Adresse du siège social" valeur={adresseLigne} />
      {/* Contact operationnel : SAISI sur l'operation, pre-rempli avec l'acheteur qui l'initie (le
          repli vit dans buildConsultationFormData). Modifier ces champs n'affecte NI le compte de
          l'utilisateur NI la fiche de l'organisme — c'est le contact de CETTE operation. */}
      {champSaisi('contactNom', 'Nom du contact opérationnel', 'ex : Marie DUPONT', 3)}
      {champSaisi('contactFonction', 'Fonction', 'ex : Responsable travaux', 3)}
      {champSaisi('contactMail', 'Mail', 'ex : m.dupont@organisme.fr', 3)}
      {champSaisi('contactTelephone', 'Téléphone', 'ex : 01 02 03 04 05', 3)}
      {/* Champs SAISIS par l'acheteur */}
      {champSaisi('signataire', 'Nom du signataire qui apparaitra sur la commande', 'ex : François VINIO, Président du CHSJ')}
      {champSaisi('modaliteFacturation', 'Modalité de facturation', 'ex : envoyer vos factures sur CHORUS, avec la mention LIS456')}
    </div>
  );
};

export default OperationAcheteurRecap;
